/**
 * Fechas de la vista Despachos — lógica PURA (la página la cablea).
 * El rango se elige con inputs de fecha nativos; el default sigue siendo
 * "hoy y mañana". Los días se agrupan y titulan en lenguaje de negocio.
 */

/** 'YYYY-MM-DD' local → Date a las 00:00 LOCAL de ese día. */
export function dateFromInput(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Date → 'YYYY-MM-DD' local (valor para <input type="date">). */
export function toInputValue(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Rango [desde 00:00, hasta 23:59:59.999] local para la query de despachos. */
export function rangoDesdeInputs(desde: string, hasta: string): { from: Date; to: Date } {
  const from = dateFromInput(desde);
  const to = dateFromInput(hasta);
  to.setDate(to.getDate() + 1);
  to.setMilliseconds(-1);
  return { from, to };
}

/** Clave del día LOCAL de una cita (para agrupar). */
function dayKey(d: Date): string {
  return toInputValue(d);
}

/** Título del día: "Hoy" / "Mañana" / "Viernes 24 jul". */
export function tituloDia(day: Date, hoy: Date): string {
  const clave = dayKey(day);
  const hoyClave = dayKey(hoy);
  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);
  if (clave === hoyClave) return 'Hoy';
  if (clave === dayKey(manana)) return 'Mañana';
  const label = day.toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'short' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export interface SeccionDia<T> {
  key: string;
  titulo: string;
  jobs: T[];
}

/** Agrupa trabajos por día local de la cita, en orden cronológico. */
export function agruparPorDia<T extends { scheduledAt?: string | null }>(
  jobs: T[],
  hoy: Date,
): SeccionDia<T>[] {
  const porDia = new Map<string, SeccionDia<T>>();
  for (const j of jobs) {
    if (!j.scheduledAt) continue;
    const d = new Date(j.scheduledAt);
    if (Number.isNaN(d.getTime())) continue;
    const key = dayKey(d);
    let seccion = porDia.get(key);
    if (!seccion) {
      seccion = { key, titulo: tituloDia(d, hoy), jobs: [] };
      porDia.set(key, seccion);
    }
    seccion.jobs.push(j);
  }
  return [...porDia.values()].sort((a, b) => a.key.localeCompare(b.key));
}
